import React, { useState, useEffect } from 'react';
import {
  Typography,
  Table,
  Button,
  Modal,
  Form,
  Input,
  DatePicker,
  Message,
  Card,
  Space,
  Popconfirm,
  Select,
  Tabs,
  TableColumnProps
} from '@arco-design/web-react';
import {
  IconPlus,
  IconEdit,
  IconDelete,
  IconRefresh,
  IconFilter,
  IconMinus
} from '@arco-design/web-react/icon';
import { useAppDispatch, useAppSelector } from '../store';
import { 
  fetchAllRecords, 
  createRecord, 
  updateRecord, 
  deleteRecord, 
  setSoftwareFilter,
  clearError 
} from '../store/slices/updateHistorySlice';
import { SoftwareUpdateRecord, DownloadLink } from '../types';
import dayjs from 'dayjs';

const { Title } = Typography;
const FormItem = Form.Item;
const TextArea = Input.TextArea;
const Option = Select.Option;
const TabPane = Tabs.TabPane;

const UpdateHistoryPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { 
    records, 
    filteredRecords, 
    currentSoftware, 
    loading, 
    error 
  } = useAppSelector((state) => state.updateHistory);

  const [modalVisible, setModalVisible] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentRecord, setCurrentRecord] = useState<SoftwareUpdateRecord | null>(null);
  const [form] = Form.useForm();
  const [tempSoftwareName, setTempSoftwareName] = useState<string>('');
  
  // 获取软件名称列表（去重）
  const softwareList = Array.from(new Set(records.map(record => record.softwareName)));

  // 初始加载所有记录
  useEffect(() => {
    dispatch(fetchAllRecords());
  }, [dispatch]);

  // 如果有错误，显示错误信息
  useEffect(() => {
    if (error) {
      Message.error(error);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  // 表格列定义
  const columns: TableColumnProps<SoftwareUpdateRecord>[] = [
    {
      title: '软件名称',
      dataIndex: 'softwareName',
      filterDropdown: ({ filterKeys, setFilterKeys, confirm }: any) => {
        return (
          <div className="arco-table-custom-filter">
            <Select
              style={{ width: 200 }}
              mode="multiple"
              value={filterKeys}
              onChange={(values) => {
                setFilterKeys(values);
                confirm();
              }}
              placeholder="选择软件名称"
            >
              {softwareList.map((software) => (
                <Option key={software} value={software}>
                  {software}
                </Option>
              ))}
            </Select>
            <div className="arco-table-custom-filter-footer">
              <Button
                size="small"
                onClick={() => {
                  setFilterKeys([]);
                  confirm();
                }}
              >
                重置
              </Button>
              <Button
                type="primary"
                size="small"
                onClick={() => confirm()}
              >
                确认
              </Button>
            </div>
          </div>
        );
      },
      onFilter: (value: string, record: SoftwareUpdateRecord) => record.softwareName === value,
    },
    {
      title: '标题',
      dataIndex: 'title',
    },
    {
      title: '版本',
      dataIndex: 'version',
    },
    {
      title: '更新日期',
      dataIndex: 'updateDate',
      render: (date: string) => dayjs(date).format('YYYY-MM-DD'),
      sorter: (a: SoftwareUpdateRecord, b: SoftwareUpdateRecord) => 
        dayjs(a.updateDate).valueOf() - dayjs(b.updateDate).valueOf(),
    },
    {
      title: '创建者',
      dataIndex: 'createdBy',
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      render: (date: string) => {
        try {
          // 尝试解析日期，如果是有效日期则格式化，否则提供默认值
          if (date && date.trim()) {
            const parsed = dayjs(date);
            return parsed.isValid() ? parsed.format('YYYY-MM-DD HH:mm') : '未知';
          }
          return '未知';
        } catch (e) {
          console.error('日期解析错误:', date, e);
          return '无效日期';
        }
      },
 
      defaultSortOrder: 'ascend', // 默认升序排列
    },
    {
      title: '操作',
      width: 150,
      render: (_: any, record: SoftwareUpdateRecord) => (
        <Space>
          <Button 
            type="text" 
            icon={<IconEdit />} 
            onClick={() => handleEdit(record)}
          >
            编辑
          </Button>
          <Popconfirm
            title="确定要删除此记录吗？"
            okText="确定"
            cancelText="取消"
            position="br"
            onOk={() => handleDelete(record.id)}
          >
            <Button type="text" status="danger" icon={<IconDelete />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  // 刷新记录列表
  const handleRefresh = () => {
    dispatch(fetchAllRecords());
  };

  // 筛选特定软件的记录
  const handleSoftwareFilter = (software: string | null) => {
    console.log('软件过滤值:', software, typeof software);
    // 修复点击全部软件时的问题
    // 将'all'标签转换为null值，表示不过滤
    const softwareValue = software === 'all' ? null : software;
    console.log('处理后的软件过滤值:', softwareValue, typeof softwareValue);
    dispatch(setSoftwareFilter(softwareValue));
  };

  // 显示创建记录对话框
  const showCreateModal = () => {
    setEditMode(false);
    setCurrentRecord(null);
    form.resetFields();
    // 为新记录初始化downloadLinks字段
    form.setFieldValue('downloadLinks', []);
    setModalVisible(true);
  };

  // 显示编辑记录对话框
  const handleEdit = (record: SoftwareUpdateRecord) => {
    setEditMode(true);
    setCurrentRecord(record);
    
    // 处理下载链接的数据格式转换
    const formValues = {
      ...record,
      updateDate: dayjs(record.updateDate),
      downloadLinks: record.downloadLinks || [] // 确保downloadLinks是数组格式
    };
    
    form.setFieldsValue(formValues);
    setModalVisible(true);
  };

  // 关闭对话框
  const hideModal = () => {
    setModalVisible(false);
  };

  // 提交表单
  const handleSubmit = async () => {
    try {
      const values = await form.validate();
      
      // 调试信息：打印表单数据
      console.log('表单原始数据:', values);
      console.log('下载链接数据:', values.downloadLinks);
      
      // 修改：调整为后端接受的数据结构
      const formattedValues = {
        ...values,
        updateDate: dayjs(values.updateDate).format('YYYY-MM-DD'),
        // 直接使用content字段，由后端处理contentFilePath
        contentFilePath: "", // 添加contentFilePath空字段
        // 不再直接设置createdBy字符串，由后端根据认证信息设置
      };

      // 调试信息：打印格式化后的数据
      console.log('格式化后的数据:', formattedValues);

      if (editMode && currentRecord) {
        // 编辑模式
        await dispatch(updateRecord({ 
          id: currentRecord.id, 
          record: formattedValues 
        })).unwrap();
        Message.success('更新记录成功');
      } else {
        // 创建模式
        await dispatch(createRecord(formattedValues)).unwrap();
        Message.success('创建记录成功');
      }
      hideModal();
    } catch (error) {
      console.error('表单验证或提交失败:', error);
      Message.error('操作失败，请检查表单数据或网络连接');
    }
  };

  // 删除记录
  const handleDelete = async (id: string) => {
    try {
      await dispatch(deleteRecord(id)).unwrap();
      Message.success('删除记录成功');
    } catch (error) {
      console.error('删除记录失败:', error);
    }
  };

  return (
    <div>
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <Title heading={4} style={{ margin: 0 }}>更新历史管理</Title>
          <Space>
            <Button 
              type="primary" 
              icon={<IconPlus />} 
              onClick={showCreateModal}
            >
              添加记录
            </Button>
            <Button 
              icon={<IconFilter />} 
              onClick={() => handleSoftwareFilter(null)}
              status={currentSoftware ? 'default' : 'warning'}
            >
              清除筛选
            </Button>
            <Button 
              icon={<IconRefresh />} 
              onClick={handleRefresh}
            >
              刷新
            </Button>
          </Space>
        </div>

        {/* 软件筛选选项卡 */}
        <Tabs type='card' onChange={handleSoftwareFilter}>
          <TabPane key="all" title="全部软件" />
          {softwareList.map(software => (
            <TabPane key={software} title={software} />
          ))}
        </Tabs>

        <Table
          loading={loading}
          columns={columns}
          data={filteredRecords}
          rowKey="id"
          border={false}
          pagination={{
            showTotal: true,
            sizeCanChange: true,
            pageSizeChangeResetCurrent: false,
          }}
        />
      </Card>

      {/* 创建/编辑记录对话框 */}
      <Modal
        title={editMode ? '编辑更新记录' : '添加更新记录'}
        visible={modalVisible}
        onOk={handleSubmit}
        onCancel={hideModal}
        autoFocus={false}
        focusLock={true}
        maskClosable={false}
        style={{ 
          width: '80vw', 
          maxWidth: '1200px',
          maxHeight: '85vh',
          top: '5vh'
        }}
      >
        <div style={{
          maxHeight: 'calc(85vh - 180px)',
          overflowY: 'auto',
          padding: '4px'
        }}>
          <Form 
            form={form}
            layout="vertical"
            autoComplete="off"
          >
          <FormItem
            label="软件名称"
            field="softwareName"
            rules={[{ required: true, message: '请输入软件名称' }]}
          >
            <Select
              placeholder="请选择或输入软件名称"
              showSearch
              allowCreate
              tokenSeparators={[]}
              disabled={editMode}
              onSearch={(value) => {
                // 使用组件状态保存当前输入值
                setTempSoftwareName(value?.trim() || '');
              }}
              onChange={(value) => {
                if (value) {
                  // 有选择值时清空临时状态
                  setTempSoftwareName('');
                  // 确保表单值被设置
                  form.setFieldValue('softwareName', value);
                }
              }}
              onBlur={() => {
                // 在blur事件中检查是否需要设置表单值
                setTimeout(() => {
                  if (tempSoftwareName && !form.getFieldValue('softwareName')) {
                    form.setFieldValue('softwareName', tempSoftwareName);
                  }
                }, 100);
              }}
              filterOption={(inputValue, option) =>
                option.props.value.toLowerCase().indexOf(inputValue.toLowerCase()) >= 0
              }
            >
              {softwareList.map((software) => (
                <Option key={software} value={software}>
                  {software}
                </Option>
              ))}
            </Select>
          </FormItem>
          <FormItem
            label="标题"
            field="title"
            rules={[{ required: false, message: '请输入更新标题' }]}
          >
            <Input placeholder="请输入更新标题" />
          </FormItem>
          <FormItem
            label="版本"
            field="version"
            rules={[{ required: true, message: '请输入版本号' }]}
          >
            <Input placeholder="请输入版本号，例如：v1.0.0" />
          </FormItem>
          <FormItem
            label="更新日期"
            field="updateDate"
            rules={[{ required: true, message: '请选择更新日期' }]}
          >
            <DatePicker placeholder="请选择更新日期" />
          </FormItem>
          <FormItem
            label="更新内容"
            field="content"
            rules={[{ required: true, message: '请输入更新内容' }]}
          >
            <TextArea placeholder="请输入更新内容" style={{ minHeight: 150 }} />
          </FormItem>
          <FormItem
            label="下载链接"
          >
            <Form.List field="downloadLinks">
              {(fields, { add, remove }) => (
                <div>
                  {fields.map((field, index) => (
                    <Card 
                      key={field.key} 
                      size="small" 
                      style={{ marginBottom: 8 }}
                      title={`下载链接 ${index + 1}`}
                      extra={
                        fields.length > 0 && (
                          <Button
                            type="text"
                            size="small"
                            icon={<IconMinus />}
                            onClick={() => remove(index)}
                            status="danger"
                          />
                        )
                      }
                    >
                      <Space direction="vertical" style={{ width: '100%' }}>
                        <FormItem
                          field={`downloadLinks.${index}.title`}
                          rules={[{ required: true, message: '请输入下载链接标题' }]}
                        >
                          <Input placeholder="请输入下载链接标题，如：官方下载、备用下载等" />
                        </FormItem>
                        <FormItem
                          field={`downloadLinks.${index}.url`}
                          rules={[
                            { required: true, message: '请输入下载地址' },
                            { type: 'url', message: '请输入有效的URL地址' }
                          ]}
                        >
                          <Input placeholder="请输入下载地址" />
                        </FormItem>
                      </Space>
                    </Card>
                  ))}
                  <Button
                    type="dashed"
                    icon={<IconPlus />}
                    onClick={() => add({ title: '', url: '' })}
                    style={{ width: '100%' }}
                  >
                    添加下载链接
                  </Button>
                </div>
              )}
            </Form.List>
          </FormItem>
        </Form>
        </div>
      </Modal>
    </div>
  );
};

export default UpdateHistoryPage; 